import { useState } from "react";
import {
  useFetchEntities,
  useGetTourGroupByIdForExternalAssignmentsDrawer,
  useUpdateExternalAssignmentsAssignedUsers,
  useUpdateExternalAssignmentsNotes,
} from "@/reactQueryHooks";
import styled from "styled-components";
import CloseIcon from "@mui/icons-material/Close";
import { RotatingLines } from "react-loader-spinner";
import moment from "moment";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import NoteIcon from "@mui/icons-material/Note";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import { ITourGroupItem } from "./TourGroupsDateSectionItem";
import { useAxios } from "@/axiosProvider";

interface IUser {
  _id: string;
  name: string;
  roles: IRole[];
}

interface IRole {
  _id: string;
  title: string;
  isExternalAssociate?: boolean;
}

interface IProduct {
  _id: string;
  title: string;
  options: IOption[];
  start_times: IStartTime[];
  crewRoles?: string[];
}

interface IOption {
  _id: string;
  title: string;
  crewRoles?: string[];
}

interface IStartTime {
  _id: string;
  time_slot: string;
}

interface IEntity {
  products: IProduct[];
  staff: IUser[];
  roles: IRole[];
}

interface IExternalAssignment {
  user_id: string;
  role_id: string;
}

interface TourGroupItemDrawerContentProps {
  tourGroupId: string;
  refetch: () => void;
  close: (e: React.MouseEvent) => void;
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;
  height: 100%;
  padding: 16px;
  background-color: #ffffff;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, sans-serif;
  font-size: 12px;
`;

const TopButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  cursor: pointer;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const HeaderItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2b2d42;
  font-weight: 500;

  i {
    color: #4361ee;
    font-size: 14px;
  }
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 4px;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  min-width: 350px;
  opacity: 1;
  position: relative;
`;

const SectionTitle = styled.div`
  font-weight: 600;
  color: #4361ee;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    font-size: 14px;
  }
`;

const DetailCard = styled.div`
  display: flex;
  flex-direction: column;
`;

const TourTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2b2d42;
  margin-bottom: 8px;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const InfoChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 500;
  background-color: #e0fbfc;
  color: #2b2d42;
`;

const StatusChip = styled.div<{ status: "cancelled" | "filled" | "partial" }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 500;
  background-color: ${(props) =>
    props.status === "cancelled"
      ? "#ffebee"
      : props.status === "filled"
      ? "#e8f5e9"
      : "#fff8e1"};
  color: ${(props) =>
    props.status === "cancelled"
      ? "#c62828"
      : props.status === "filled"
      ? "#2e7d32"
      : "#f57c00"};
`;

const NotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NoteItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 4px;
  background: #f2f3b8;
`;

const NoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NoteAuthor = styled.span`
  font-weight: 600;
  color: #1568bb;
  font-size: 11px;
`;

const NoteDate = styled.span`
  color: #777;
  font-size: 10px;
`;

const NoteBody = styled.div`
  color: #333;
  font-size: 12px;
  white-space: pre-wrap;
`;

const NoteActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const NewNoteForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 4px;
  background: #f8f9fa;
`;

const NoteTextarea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  resize: vertical;
  min-height: 60px;
  font-family: inherit;
  font-size: 12px;
  outline: none;

  &:focus {
    border-color: #4361ee;
    box-shadow: 0 0 0 2px rgba(67, 97, 238, 0.15);
  }
`;

const NoteFormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

//--------------------------------------------------------------

// Main Component
const TourGroupItemDrawerContent = ({
  tourGroupId,
  refetch,
  close,
}: TourGroupItemDrawerContentProps) => {
  const { user } = useAxios();
  const userIsAdmin = user.isAdmin;
  const userIsPermittedToEditGuidePlan =
    user.permissions.editGuidePlan || userIsAdmin;

  const { data: entitiesData } = useFetchEntities() as {
    data: { data: IEntity };
  };

  const { mutateAsync: updateExternalAssignmentsAssignedUsers } =
    useUpdateExternalAssignmentsAssignedUsers() as any;

  const { mutateAsync: updateExternalAssignmentsNotes } =
    useUpdateExternalAssignmentsNotes() as any;

  const {
    data: tourGroup,
    isLoading,
    isError,
    refetch: refetchTourGroup,
  } = useGetTourGroupByIdForExternalAssignmentsDrawer(tourGroupId) as {
    data: ITourGroupItem;
    isLoading: boolean;
    isError: boolean;
    refetch: () => void;
  };
  const entities = entitiesData?.data;

  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");

  const product = entities?.products?.find(
    (p) => p._id === tourGroup?.product_id
  );
  const option = product?.options?.find((o) => o._id === tourGroup?.option_id);
  const startTime = product?.start_times?.find(
    (st) => st._id === tourGroup?.start_time_id
  );

  // Find roles that are relevant for this option and are marked as external associates
  const eligibleRoleIds = option?.crewRoles || [];
  const externalAssociateRoles =
    entities?.roles?.filter(
      (role) => eligibleRoleIds.includes(role._id) && role.isExternalAssociate
    ) || [];

  // Find users that have at least one of the eligible external associate roles
  const eligibleUsers =
    entities?.staff?.filter((user) => {
      const userRoleIds = user.roles.map((role) => role._id);
      return externalAssociateRoles.some((role) =>
        userRoleIds.includes(role._id)
      );
    }) || [];

  eligibleUsers?.sort((a, b) => a.name.localeCompare(b.name));

  const usersWithRoles = tourGroup?.external_assignments?.map((assignment) => {
    const user = entities.staff.find((u) => u._id === assignment.user_id);
    const role = entities.roles.find((r) => r._id === assignment.role_id);
    return {
      user: user || { _id: "", name: "Unknown", roles: [] },
      role: role || { _id: "", title: "Unknown" },
    };
  });

  const uniqueUsersWithRoles = usersWithRoles?.filter(
    (item, index, self) =>
      index === self.findIndex((t) => t.user._id === item.user._id)
  );

  const handleAssignedUsersChange = async (
    _event: React.SyntheticEvent<Element, Event>,
    newValues: { user: IUser; role: IRole }[]
  ) => {
    try {
      const uniqueUsers = newValues.filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.user._id === value.user._id)
      );

      const formattedAssignments: IExternalAssignment[] = uniqueUsers.map(
        (item) => ({
          role_id: item.role._id,
          user_id: item.user._id,
        })
      );

      const payload = {
        tour_group_id: tourGroupId,
        external_assignments: formattedAssignments,
      };

      await updateExternalAssignmentsAssignedUsers(payload);
      refetch();
      refetchTourGroup();
    } catch (e) {
      console.error("Error in handleAssignedUsersChange:", e);
    }
  };

  // Note editing functions
  const startEditingNote = (noteId: string, noteText: string) => {
    setEditingNoteId(noteId);
    setEditingNoteText(noteText);
  };

  const cancelEditingNote = () => {
    setEditingNoteId(null);
    setEditingNoteText("");
  };

  const saveEditedNote = async (noteId: string) => {
    try {
      if (tourGroup?.external_assignments_notes) {
        const updatedNotes = tourGroup?.external_assignments_notes.map((note) =>
          note._id === noteId ? { ...note, body: editingNoteText } : note
        );

        const payload = {
          tour_group_id: tourGroupId,
          external_assignments_notes: updatedNotes,
        };

        await updateExternalAssignmentsNotes(payload);
        refetch();
        refetchTourGroup();
      }

      setEditingNoteId(null);
    } catch (e) {
      console.error("Error in saveEditedNote:", e);
    }
  };

  const deleteNote = async (noteId: string) => {
    try {
      if (tourGroup?.external_assignments_notes) {
        const updatedNotes = tourGroup?.external_assignments_notes.filter(
          (note) => note._id !== noteId
        );

        const payload = {
          tour_group_id: tourGroupId,
          external_assignments_notes: updatedNotes,
        };

        await updateExternalAssignmentsNotes(payload);
        refetch();
        refetchTourGroup();
      }
    } catch (e) {
      console.error("Error in deleteNote:", e);
    }
  };

  const startAddingNote = () => {
    setIsAddingNote(true);
    setNewNoteText("");
  };

  const cancelAddingNote = () => {
    setIsAddingNote(false);
    setNewNoteText("");
  };

  const saveNewNote = async () => {
    try {
      if (!newNoteText.trim()) {
        return;
      }

      const newNote = {
        user_id: user._id,
        body: newNoteText,
      };

      const updatedNotes = [
        ...(tourGroup?.external_assignments_notes || []),
        newNote,
      ];
      const payload = {
        tour_group_id: tourGroupId,
        external_assignments_notes: updatedNotes,
      };
      await updateExternalAssignmentsNotes(payload);
      refetch();
      refetchTourGroup();
      setIsAddingNote(false);
      setNewNoteText("");
    } catch (e) {
      console.error("Error in saveNewNote:", e);
    }
  };

  return (
    <Container>
      {isLoading && (
        <LoadingContainer>
          <RotatingLines strokeColor="black" width={"15px"} />
        </LoadingContainer>
      )}

      {!isLoading && (isError || !tourGroup?.tour_group_id) && (
        <LoadingContainer>
          <span
            style={{
              color: "indianred",
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            Error loading tour group data
          </span>
        </LoadingContainer>
      )}

      {!isLoading && tourGroup?.tour_group_id && (
        <>
          <TopButtonsRow>
            <IconButton onClick={close} size="small">
              <CloseIcon />
            </IconButton>
          </TopButtonsRow>

          <Header>
            <HeaderItem>
              <i className="fas fa-calendar-day"></i>
              <span>{moment(tourGroup.date).format("ddd, DD MMM YYYY")}</span>
            </HeaderItem>
          </Header>

          <ScrollableContent>
            <SectionContainer>
              <DetailCard>
                <TourTitle>{product?.title || "Unknown Tour"}</TourTitle>
                <ChipsContainer>
                  <InfoChip>
                    <i className="fas fa-tag" style={{ fontSize: "10px" }}></i>
                    {option?.title || "Unknown Option"}
                  </InfoChip>
                  <InfoChip>
                    <i
                      className="fas fa-clock"
                      style={{ fontSize: "10px" }}
                    ></i>
                    {startTime?.time_slot || "Unknown Time"}
                  </InfoChip>
                  <InfoChip>
                    <i
                      className="fas fa-hashtag"
                      style={{ fontSize: "10px" }}
                    ></i>
                    Group {tourGroup.index}
                  </InfoChip>
                  <InfoChip>
                    <i
                      className="fas fa-users"
                      style={{ fontSize: "10px" }}
                    ></i>
                    {tourGroup.seatsCount} Seats
                  </InfoChip>
                  <StatusChip
                    status={
                      tourGroup.isCancelled
                        ? "cancelled"
                        : tourGroup.allRolesFilled
                        ? "filled"
                        : "partial"
                    }
                  >
                    <i
                      className={`fas fa-${
                        tourGroup.isCancelled
                          ? "ban"
                          : tourGroup.allRolesFilled
                          ? "check-circle"
                          : "exclamation-circle"
                      }`}
                      style={{ fontSize: "10px" }}
                    ></i>
                    {tourGroup.isCancelled
                      ? "Cancelled"
                      : tourGroup.allRolesFilled
                      ? "Fully Assigned"
                      : "Not Assigned"}
                  </StatusChip>
                </ChipsContainer>
              </DetailCard>
            </SectionContainer>

            {/* External Associates Section */}
            <SectionContainer>
              <SectionTitle>
                <i className="fas fa-users"></i>
                External Assignments
              </SectionTitle>
              <Autocomplete
                disableClearable
                multiple
                options={eligibleUsers
                  ?.filter(
                    (user) =>
                      !uniqueUsersWithRoles?.some(
                        (item) => item.user._id === user._id
                      )
                  )
                  .map((user) => {
                    // Find the first eligible role for this user
                    const eligibleRole = user.roles.find((role) =>
                      externalAssociateRoles.some((r) => r._id === role._id)
                    );
                    const roleData = eligibleRole
                      ? entities?.roles?.find((r) => r._id === eligibleRole._id)
                      : null;
                    return {
                      user,
                      role: roleData || { _id: "", title: "Unknown" },
                    };
                  })}
                value={uniqueUsersWithRoles}
                onChange={handleAssignedUsersChange}
                getOptionLabel={(option) =>
                  `${option.user.name} (${option.role.title})`
                }
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={`${option.user.name} (${option.role.title})`}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    //placeholder="Select external associates"
                  />
                )}
                size="small"
              />
            </SectionContainer>

            {/* Notes Section */}
            <SectionContainer>
              <SectionTitle>
                <i className="fas fa-sticky-note"></i>
                Notes
                <Button
                  startIcon={<AddIcon />}
                  size="small"
                  variant="text"
                  onClick={startAddingNote}
                  style={{ marginLeft: "auto", fontSize: "11px" }}
                  disabled={isAddingNote}
                >
                  Add Note
                </Button>
              </SectionTitle>

              {/* Add Note Form */}
              {isAddingNote && (
                <NewNoteForm>
                  <NoteTextarea
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    placeholder="Enter your note here..."
                    autoFocus
                  />
                  <NoteFormActions>
                    <Button
                      startIcon={<CancelIcon />}
                      size="small"
                      variant="text"
                      onClick={cancelAddingNote}
                      style={{ fontSize: "11px" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      startIcon={<SaveIcon />}
                      size="small"
                      variant="contained"
                      onClick={saveNewNote}
                      style={{ fontSize: "11px" }}
                    >
                      Save
                    </Button>
                  </NoteFormActions>
                </NewNoteForm>
              )}

              {/* Notes List */}
              <NotesList>
                {!isAddingNote &&
                  (!tourGroup.external_assignments_notes ||
                    tourGroup.external_assignments_notes.length === 0) && (
                    <div
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        color: "#6c757d",
                        backgroundColor: "rgba(67, 97, 238, 0.05)",
                        borderRadius: "8px",
                      }}
                    >
                      No notes yet
                    </div>
                  )}

                {tourGroup.external_assignments_notes?.map((note) => {
                  const author = entities?.staff?.find(
                    (user) => user._id === note.user_id
                  );

                  return (
                    <NoteItem key={note._id}>
                      <NoteIcon
                        sx={{
                          //color yellow-ish
                          color: "#f6ab6c",
                          fontSize: "16px",
                        }}
                      />
                      <NoteHeader>
                        <NoteAuthor>{author?.name || "Unknown"}</NoteAuthor>
                        <NoteDate>{moment(note.date).fromNow()}</NoteDate>
                      </NoteHeader>

                      {editingNoteId === note._id ? (
                        <>
                          <NoteTextarea
                            value={editingNoteText}
                            onChange={(e) => setEditingNoteText(e.target.value)}
                            autoFocus
                          />
                          <NoteActions>
                            <IconButton
                              size="small"
                              onClick={() => cancelEditingNote()}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => saveEditedNote(note._id as string)}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </NoteActions>
                        </>
                      ) : (
                        <>
                          <NoteBody>{note.body}</NoteBody>
                          <NoteActions>
                            <IconButton
                              size="small"
                              onClick={() =>
                                startEditingNote(note._id as string, note.body)
                              }
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => deleteNote(note._id as string)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </NoteActions>
                        </>
                      )}
                    </NoteItem>
                  );
                })}
              </NotesList>
            </SectionContainer>
          </ScrollableContent>
        </>
      )}
    </Container>
  );
};

export default TourGroupItemDrawerContent;
